type MessagePolicy = Readonly<{ sensitive: boolean; awaitHandling: boolean }>;

const ordinary: MessagePolicy = Object.freeze({ sensitive: false, awaitHandling: false });
const privateTicket: MessagePolicy = Object.freeze({ sensitive: true, awaitHandling: true });

// Policy belongs to the envelope identity, not individual logging/capture paths.
// It does not add fields to the native or WebSocket wire contracts. Register
// additional private protocols here so every transport applies the same policy.
const nativePolicies = new Map<string, MessagePolicy>([
  ['gameSaveStorage', privateTicket],
  ['macroPackageUpload', privateTicket],
  ['rawPackageSocket', privateTicket],
  ...['macroGraph', 'macroGraphEvent', 'macroGraphSession', 'macroGraphRegistration', 'macroGraphTraceHistory',
    'macroGraphSimulation', 'macroSequence', 'macroSequenceEvent', 'macroLocalGraphService', 'macroLocalGraphCalibrate']
    .map(action => [action, privateTicket] as [string, MessagePolicy]),
]);
const websocketPolicies = new Map<string, MessagePolicy>([
  ['HOST_GAME_SAVE_STORAGE_REQUEST', privateTicket],
  ['HOST_GAME_SAVE_STORAGE_REPLY', privateTicket],
  ['RAWPACKAGE_UPLOAD_BEGIN', privateTicket],
  ['RAWPACKAGE_UPLOAD_COMPLETE', privateTicket],
  ['RAWPACKAGE_UPLOAD_GRANTED', privateTicket],
  ...['MACROGRAPH_CONTROL', 'MACROGRAPH_RESPONSE', 'MACROGRAPH_EVENT', 'MACROGRAPH_SERVICE_CONTROL', 'MACROGRAPH_SERVICE_RESPONSE']
    .map(type => [type, privateTicket] as [string, MessagePolicy]),
]);

function envelopePolicy(message: unknown): MessagePolicy {
  if (!message || typeof message !== 'object') return ordinary;
  const envelope = message as { header?: { action?: unknown }; type?: unknown };
  const action = envelope.header?.action;
  if (typeof action === 'string' && nativePolicies.has(action)) return nativePolicies.get(action)!;
  return typeof envelope.type === 'string' ? websocketPolicies.get(envelope.type) ?? ordinary : ordinary;
}

function messagePolicy(message: unknown): MessagePolicy {
  if (typeof message !== 'string') return envelopePolicy(message);
  try { return envelopePolicy(JSON.parse(message)); }
  catch {
    // Malformed envelopes can still contain credentials. Read only JSON string
    // properties, including escaped keys/values; never print the original text.
    const properties = /("(?:\\.|[^"\\])*")\s*:\s*("(?:\\.|[^"\\])*")/g;
    for (const property of message.matchAll(properties)) {
      try {
        const key = JSON.parse(property[1]), value = JSON.parse(property[2]);
        const policy = key === 'action' ? nativePolicies.get(value) :
          key === 'type' ? websocketPolicies.get(value) : undefined;
        if (policy?.sensitive) return policy;
      } catch { /* An invalid string property cannot identify a protocol. */ }
    }
    return ordinary;
  }
}

/** Safe to call before validation, including on incomplete serialized envelopes. */
export function isSensitiveMessage(message: unknown): boolean {
  return messagePolicy(message).sensitive;
}

/** Generic dispatch policy; private request/reply work must settle before returning. */
export function shouldAwaitMessageHandling(message: unknown): boolean {
  return messagePolicy(message).awaitHandling;
}
